import { useState, FormEvent } from 'react';
import { track } from '@vercel/analytics';
import { supabase } from '@/lib/customSupabaseClient';

// Campaign attribution. utm_source is what Vercel Web Analytics parses natively,
// so it shows up in the dashboard's UTM Parameters tab without any work on our side.
// ?src= is kept as a shorthand fallback. Whichever we resolve is what lands in
// waitlist.source, so the Vercel visit number and the Supabase signup number use
// the same label and can be divided by each other.
const SRC_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;
function resolveSource(fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const q = new URLSearchParams(window.location.search);
  for (const key of ['utm_source', 'src']) {
    const clean = (q.get(key) || '').trim().toLowerCase();
    if (SRC_RE.test(clean)) return clean;
  }
  return fallback;
}

type WaitlistFormProps = {
  source: string;
  onSuccess?: (count: number) => void;
  showPosition?: boolean;
  onDark?: boolean;
};

const WaitlistForm: React.FC<WaitlistFormProps> = ({ source, onSuccess, showPosition, onDark = false }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [memberNumber, setMemberNumber] = useState<number | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;

    setSubmitting(true);
    setError(false);

    const resolvedSource = resolveSource(source);

    const { data, error: rpcError } = await supabase
      .rpc('join_waitlist', {
        p_email: email.trim().toLowerCase(),
        p_name: null,
        p_source: resolvedSource,
      });

    setSubmitting(false);

    if (rpcError) {
      track('waitlist_error', { source: resolvedSource });
      setError(true);
      return;
    }

    track('waitlist_signup', { source: resolvedSource });

    const count = typeof data === 'number' ? data : Number(data);
    if (Number.isFinite(count)) {
      setMemberNumber(count);
      onSuccess?.(count);
    }

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <p className={`text-lg ${onDark ? 'text-maslow-cream' : 'text-maslow-navy'} font-medium italic`}>
        {showPosition && typeof memberNumber === 'number'
          ? `You're #${memberNumber} in line. I'll be in touch as it gets closer.`
          : "You're on the list. You'll hear about it here first."}
        <br />
        <span className="not-italic text-base font-normal">
          Watch it get built on Instagram:{' '}
          <a
            href="https://www.instagram.com/maslow.nyc"
            target="_blank"
            rel="noopener noreferrer"
            className="text-maslow-gold underline hover:opacity-80 transition-opacity"
          >
            @maslow.nyc
          </a>
        </span>
      </p>
    );
  }

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="flex max-w-md mx-auto overflow-hidden rounded-lg border border-[color-mix(in_srgb,var(--gold)_40%,transparent)] bg-white"
      >
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(false);
          }}
          placeholder="your@email.com"
          disabled={submitting}
          className="flex-1 px-4 py-3.5 text-maslow-navy bg-white outline-none border-none"
        />
        {/* a11y: white on gold was 2.07:1 (WCAG AA needs 4.5). Navy on gold is 6.96:1. */}
        <button
          type="submit"
          disabled={submitting}
          className="bg-maslow-gold hover:bg-maslow-gold-hover text-maslow-navy font-semibold uppercase tracking-wider text-sm px-5 py-3.5 whitespace-nowrap transition-colors disabled:opacity-70"
        >
          {submitting ? 'Joining…' : 'Count me in'}
        </button>
      </form>

      {error && (
        <p className={`mt-4 text-sm ${onDark ? 'text-[color-mix(in_srgb,var(--cream)_85%,transparent)]' : 'text-[color-mix(in_srgb,var(--navy)_70%,transparent)]'}`}>
          Something went wrong.{' '}
          <button
            type="button"
            onClick={() => setError(false)}
            className="text-maslow-gold underline hover:opacity-80 transition-opacity"
          >
            Try again?
          </button>
        </p>
      )}
    </div>
  );
};

export default WaitlistForm;
